import { useState, useMemo } from "react";
import { ArrowUpNarrowWide, ArrowDownWideNarrow } from "lucide-react";

/**
 * useSortDir — one-click ascending/descending toggle for admin lists.
 * Default: "asc" (whatever order the API returns).
 * Toggling swaps between "asc" and "desc" which reverses the list.
 */
export function useSortDir(initial = "asc") {
  const [dir, setDir] = useState(initial);
  const toggle = () => setDir(d => (d === "asc" ? "desc" : "asc"));
  return { dir, toggle, setDir };
}

/**
 * applySort — pure helper. Returns a reversed copy if desc.
 * Callers keep whatever primary sort the API provides; this toggle only flips direction.
 */
export function applySort(list, dir) {
  if (!Array.isArray(list)) return list;
  return dir === "desc" ? [...list].reverse() : list;
}

export default function SortToggle({ dir, onToggle, testId = "sort-toggle" }) {
  const label = useMemo(() => (dir === "desc" ? "Descending" : "Ascending"), [dir]);
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#1E2C5A] border border-[#1E2C5A]/30 hover:bg-[#1E2C5A] hover:text-white transition-colors px-3 py-1.5"
      data-testid={testId}
      title={`Click to switch to ${dir === "desc" ? "ascending" : "descending"} order`}
      aria-label={`Sort ${label} — click to toggle`}
    >
      {dir === "desc" ? (
        <ArrowDownWideNarrow className="w-3.5 h-3.5" strokeWidth={1.5} />
      ) : (
        <ArrowUpNarrowWide className="w-3.5 h-3.5" strokeWidth={1.5} />
      )}
      {label}
    </button>
  );
}
